import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Link,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../context/AuthContext";
import { useModal } from "../context/ModalContext";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: 0,
  boxShadow: 24,
  px: 4,
  pb: 4,
  pt: 4,
};

const SignupModal = ({ open, onClose }) => {
  const { signup } = useAuth();
  const { openLogin } = useModal();

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleClose = () => {
    setFirstname("");
    setLastname("");
    setEmail("");
    setEmailError("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError("");
    setSuccess("");
    onClose();
  };

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validatePassword = (value) => {
    let types = 0;
    if (/[A-Z]/.test(value)) types++;
    if (/[a-z]/.test(value)) types++;
    if (/[0-9]/.test(value)) types++;
    if (/[^A-Za-z0-9]/.test(value)) types++;
    return types >= 2;
  };

  const allPasswordChecksPass =
    password.length >= 12 &&
    validatePassword(password) &&
    password === confirmPassword;

  const handleSignup = async () => {
    setError("");
    setSuccess("");

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (password.length < 12) {
      setError("Password must be at least 12 characters long");
      return;
    }

    if (!validatePassword(password)) {
      setError(
        "Password must include at least two of the following: uppercase, lowercase, numbers, or symbols."
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    signup({ firstname, lastname, email });
    setSuccess("Signup successful!");
    handleClose();
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError("");
    setError("");
  };

  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError("Please enter a valid email address");
    }
  };

  const isFormValid =
    firstname.trim() &&
    lastname.trim() &&
    email.trim() &&
    password.trim() &&
    confirmPassword.trim() &&
    !emailError &&
    validateEmail(email);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      BackdropProps={{
        sx: { backgroundColor: "rgba(0,0,0,0.7)" },
      }}
    >
      <Box sx={modalStyle}>
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            padding: "4px",
            cursor: "pointer",
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 12,
            left: 0,
            width: "100%",
            textAlign: "center",
            fontFamily: "Poppins, sans-serif",
            fontSize: "32px",
            fontWeight: 400,
            color: "#142434",
          }}
        >
          Sign up
        </Typography>

        {/* Divider */}
        <Divider
          sx={{
            position: "absolute",
            top: 70,
            left: 0,
            width: "100%",
            borderColor: "#489596",
          }}
        />

        <Box display="flex" flexDirection="column" gap={2} mt={7}>
          {/* First Name */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans" }}>
              First Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter your first name"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              inputProps={{
                style: { fontFamily: "PT Sans", fontSize: 14, color: "#999C9F" },
              }}
            />
          </Box>

          {/* Last Name */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans" }}>
              Last Name
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter your last name"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              inputProps={{
                style: { fontFamily: "PT Sans", fontSize: 14, color: "#999C9F" },
              }}
            />
          </Box>

          {/* Email */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans" }}>
              Email
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter your email"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              inputProps={{
                style: { fontFamily: "PT Sans", fontSize: 14, color: "#999C9F" },
              }}
            />
          </Box>

          {/* Password */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans" }}>
              Password
            </Typography>
            <TextField
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              inputProps={{
                style: { fontFamily: "PT Sans", fontSize: 14, color: "#999C9F" },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Confirm Password */}
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontFamily: "PT Sans" }}>
              Confirm Password
            </Typography>
            <TextField
              fullWidth
              size="small"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              inputProps={{
                style: { fontFamily: "PT Sans", fontSize: 14, color: "#999C9F" },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Messages */}
          <Box sx={{ minHeight: "40px", whiteSpace: "pre-wrap" }}>
            {(emailError || error || success || allPasswordChecksPass) && (
              <Typography
                variant="body2"
                sx={{
                  color: emailError
                    ? "error.main"
                    : error
                    ? "error.main"
                    : allPasswordChecksPass
                    ? "success.main"
                    : "success.main",
                }}
              >
                {emailError ||
                  error ||
                  success ||
                  (allPasswordChecksPass ? "Passwords match" : "")}
              </Typography>
            )}
          </Box>

          {/* Sign Up button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={!isFormValid}
              onClick={handleSignup}
              sx={{
                width: "190px",
                height: "40px",
                fontFamily: "Poppins, sans-serif",
                fontSize: "18px",
                fontWeight: 600,
                color: "#FFFFFF",
                boxShadow: "none",
                backgroundColor: isFormValid ? "#489596" : "#999C9F",
                "&:hover": {
                  backgroundColor: isFormValid ? "#3a7b7f" : "#999C9F",
                  boxShadow: "none",
                },
                textTransform: "none",
              }}
            >
              Sign Up
            </Button>
          </Box>

          {/* Link to Login */}
          <Box mt={2}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                fontFamily: "PT Sans",
                fontSize: "16px",
              }}
            >
              Already have an account?{" "}
              <Link
                href="#"
                onClick={() => {
                  handleClose();
                  openLogin();
                }}
                underline="none"
                sx={{
                  color: "#489596",
                  fontFamily: "PT Sans",
                  fontSize: "16px",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Log in
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SignupModal;
